import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, catchError, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';

//primeng
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SkeletonModule } from 'primeng/skeleton';

//app
import { MenuItems } from '../../../menu';
import { CompanyService, ForeignNationalService, ShareDataService } from '../../../../services';

@Component({
  selector: 'app-header-breadcrumb',
  standalone: true,
  imports: [CommonModule, BreadcrumbModule, SkeletonModule],
  template: `
    <!-- Loading skeleton -->
    <div *ngIf="isLoading" class="breadcrumb-skeleton">
      <p-skeleton size="16px" />
      <span class="separator">></span>
      <p-skeleton width="120px" height="16px" />
      <span class="separator">></span>
      <p-skeleton width="180px" height="16px" />
    </div>

    <!-- Actual breadcrumb -->
    <p-breadcrumb *ngIf="!isLoading" [model]="items" [home]="home" />
  `,
  styles: [
    `
      :host ::ng-deep {
        caret-color: transparent;

        .breadcrumb-skeleton {
          display: flex;
          align-items: center;
          gap: 8px;

          .separator {
            color: var(--p-primary-color);
            font-weight: 500;
          }
        }

        .p-breadcrumb {
          border: none;
          padding: 0;

          .p-breadcrumb-list {
            li.p-breadcrumb-separator {
              color: var(--p-primary-color);
            }

            .p-breadcrumb-item-link[href] {
              color: var(--p-primary-color);
              .p-breadcrumb-item-icon {
                color: var(--p-primary-color);
              }
            }
          }
        }
      }
    `,
  ],
  providers: [CompanyService, ForeignNationalService],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  isLoading = false;
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/',
    styleClass: 'home-icon-only',
  };

  private destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly shareDataService: ShareDataService,
    private readonly companyService: CompanyService,
    private readonly foreignNationalService: ForeignNationalService
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateBreadcrumbs();
    });
  }

  private updateBreadcrumbs() {
    const currentPath = this.router.url.split('?')[0];
    const pathSegments = currentPath.split('/').filter((segment) => segment);

    if (pathSegments.length === 0) {
      this.items = [{ label: 'Home' }];
      return;
    }

    this.processBreadcrumbsByPath(pathSegments);
  }

  private processBreadcrumbsByPath(pathSegments: string[]): void {
    const firstSegment = pathSegments[0];

    if (firstSegment === 'advanced-search') {
      this.items = [{ label: 'Advanced Search' }];
      return;
    }

    if (firstSegment === 'matter') {
      this.processMatterBreadcrumbs(pathSegments);
    } else if (firstSegment === 'company') {
      this.processCompanyBreadcrumbs(pathSegments);
    } else if (firstSegment === 'foreign-national') {
      this.processForeignNationalBreadcrumbs(pathSegments);
    } else if (firstSegment === 'admin') {
      this.processAdminBreadcrumbs(pathSegments);
    } else {
      this.items = this.buildBreadcrumbs(pathSegments);
    }
  }

  private processMatterBreadcrumbs(pathSegments: string[]): void {
    if (this.isDetailView(pathSegments[1])) {
      this.isLoading = true;
      this.buildMatterBreadcrumbs(pathSegments).subscribe((breadcrumbs) => {
        this.items = breadcrumbs;
        this.isLoading = false;
      });
    } else {
      this.items = this.buildBreadcrumbs(pathSegments);
      this.isLoading = false;
    }
  }

  private processCompanyBreadcrumbs(pathSegments: string[]): void {
    if (this.isDetailView(pathSegments[1])) {
      this.isLoading = true;
      this.buildCompanyBreadcrumbs(pathSegments).subscribe((breadcrumbs) => {
        this.items = breadcrumbs;
        this.isLoading = false;
      });
    } else {
      this.items = this.buildBreadcrumbs(pathSegments);
      this.isLoading = false;
    }
  }

  private processForeignNationalBreadcrumbs(pathSegments: string[]): void {
    if (this.isDetailView(pathSegments[1])) {
      this.isLoading = true;
      this.buildForeignNationalBreadcrumbs(pathSegments).subscribe((breadcrumbs) => {
        this.items = breadcrumbs;
        this.isLoading = false;
      });
    } else {
      this.items = this.buildBreadcrumbs(pathSegments);
      this.isLoading = false;
    }
  }

  private processAdminBreadcrumbs(pathSegments: string[]): void {
    // Always start with "Admin" (no link)
    const adminBreadcrumbs: MenuItem[] = [
      {
        label: 'Admin'
      }
    ];

    // Build remaining breadcrumbs from path segments (excluding 'admin')
    const remainingBreadcrumbs = this.buildBreadcrumbs(pathSegments);
    this.items = [...adminBreadcrumbs, ...remainingBreadcrumbs];
    this.isLoading = false;
  }

  private isDetailView(segment: string): boolean {
    return !!segment && segment !== 'list' && segment !== 'add';
  }

  private buildBreadcrumbs(pathSegments: string[]): MenuItem[] {
    const breadcrumbs: MenuItem[] = [];
    let currentPath = '';
    let currentItems = MenuItems;

    if (pathSegments.length > 0) {
      pathSegments.forEach((segment) => {
        currentPath += `/${segment}`;
        const matchingItem = this.findMatchingMenuItem(currentItems, currentPath);

        if (matchingItem) {
          breadcrumbs.push({
            label: matchingItem.label,
            routerLink: matchingItem['route'] ?? currentPath,
          });

          if (matchingItem.items) {
            currentItems = matchingItem.items;
          }
        }
      });
    }

    return breadcrumbs;
  }

  private buildMatterBreadcrumbs(pathSegments: string[]): Observable<MenuItem[]> {
    const breadcrumbs: MenuItem[] = [];

    return this.shareDataService.getMatterByCode(pathSegments[1]).pipe(
      map((matter) => {
        // Only add company breadcrumb if companyBusinessCode exists
        if (matter.companyBusinessCode) {
          breadcrumbs.push({
            label: matter.companyName ?? '',
            icon: 'pi pi-building',
            routerLink: `/company/${matter.companyBusinessCode}/dashboard`,
          });
        }
        breadcrumbs.push({
          label: matter.foreignNational?.fullName ?? '',
          icon: 'pi pi-user',
          routerLink: `/foreign-national/${matter.foreignNational?.businessCode}/dashboard`,
        });
        breadcrumbs.push({
          label: matter.matterName + ' ' + matter.businessCode,
          icon: 'pi pi-pen-to-square',
        });
        return breadcrumbs;
      }),
      catchError((error) => {
        console.error('Failed to load matter details:', error);
        return of(this.buildBreadcrumbs(pathSegments));
      })
    );
  }

  private buildCompanyBreadcrumbs(pathSegments: string[]): Observable<MenuItem[]> {
    const breadcrumbs: MenuItem[] = [
      {
        label: 'Companies',
        icon: 'pi pi-building',
        routerLink: '/company',
      },
    ];

    return this.companyService.getByCode(pathSegments[1]).pipe(
      map((company) => {
        breadcrumbs.push({
          label: company.legalName ?? '',
          icon: 'pi pi-building',
        });
        return breadcrumbs;
      }),
      catchError((error) => {
        console.error('Failed to load company details:', error);
        return of(this.buildBreadcrumbs(pathSegments));
      })
    );
  }

  private buildForeignNationalBreadcrumbs(pathSegments: string[]): Observable<MenuItem[]> {
    const breadcrumbs: MenuItem[] = [
      {
        label: 'Foreign Nationals',
        icon: 'pi pi-user',
        routerLink: '/foreign-national',
      },
    ];

    return this.foreignNationalService.getFNByCode(pathSegments[1]).pipe(
      map((foreignNational) => {
        breadcrumbs.push({
          label: foreignNational.fullName ?? '',
          icon: 'pi pi-building',
        });
        return breadcrumbs;
      }),
      catchError((error) => {
        console.error('Failed to load foreign national details:', error);
        return of(this.buildBreadcrumbs(pathSegments));
      })
    );
  }

  private findMatchingMenuItem(items: MenuItem[], path: string): MenuItem | undefined {
    for (const item of items) {
      if (item['route']?.[0] === path) {
        return item;
      }

      if (item.items) {
        const match = this.findMatchingMenuItem(item.items, path);
        if (match) {
          return match;
        }
      }
    }
    return undefined;
  }

  ngOnInit(): void {
    // Subscribe to lastBreadcrumb$ to dynamically add/remove the last breadcrumb item
    this.shareDataService.lastBreadcrumb$
      .pipe(takeUntil(this.destroy$))
      .subscribe(name => {
        // Remove any existing dynamic breadcrumb
        this.items = this.items.filter(item => !item['isDynamic']);

        // Add new dynamic breadcrumb if name is provided
        if (name) {
          this.items.push({
            label: name,
            isDynamic: true
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
